import Label, { LabelProps } from '@/components/elements/Label/Label';
import { ListItem } from '@/components/elements/lists';
import { ListItemProps } from '@/components/elements/lists/ListItem/ListItem';
import { CompanyAvatar } from '@/components/elements/redesign/Avatar';
import Icon from '@/components/icons/Icon';
import { _s } from '@/locale';
import { AppointmentStatus } from '@/types/appointment';
import Header from '@/components/elements/Header/Header';
import { PersonAvatar } from '@/components/elements/redesign/Avatar';

type Image = {
  src: string;
  alt?: string;
};

type BookingListContentProps = {
  image?: Image;
  placeName?: string;
  dateTime?: string;
  performerAvatar?: string;
  performer?: string;
  services?: string;
  label?: LabelProps;
  companyAvatarPosition?: 'left' | 'right';
};

export type BookingListItemProps = BookingListContentProps & {
  onClick?: () => void;
  src?: ListItemProps['to'];
  status?: AppointmentStatus;
} & Pick<ListItemProps, 'leftPadding' | 'rightPadding' | 'underline'>;

const bookingStatusLabelMap: {
  [key in AppointmentStatus]?: { label: string; className: string };
} = {
  1: { label: _s('booked'), className: 'bg-brand-700 text-white' },
  101: { label: _s('confirmed'), className: 'bg-black-100 text-black-900' },
  2: { label: _s('canceled'), className: 'bg-danger-700 text-white' },
  102: { label: _s('canceled'), className: 'bg-danger-700 text-white' },
};

// TODO
// This component has new design but will temporarily differ from other places in App, CheckoutDetails
//  that presents the same information.
const BookingListContent = ({
  placeName,
  performer,
  services,
  dateTime,
  label,
  performerAvatar,
}: BookingListContentProps) => {
  return (
    <div className={'space-y-md'}>
      {label && <Label {...label} />}
      {placeName && <Header h="h5" size={'xl'} wrapperElement="header" fontWeight={'400'} label={placeName} />}
      <div className="mt-3 flex flex-col flex-wrap gap-4">
        {dateTime && <p className="text-sm_secondary text-m">{dateTime}</p>}
        <div className="flex items-center">
          {performerAvatar ? (
            <PersonAvatar className="mr-2" alt={performer} size="sm" imgUrl={performerAvatar} />
          ) : null}
          <p className="text-m text-fg_secondary line-clamp-[1] overflow-hidden text-ellipsis break-all">{performer}</p>
        </div>
        {services && <p className="text-fg_secondary text-m">{services}</p>}
      </div>
    </div>
  );
};

const BookingListItem = ({
  companyAvatarPosition = 'left',
  image,
  performerAvatar,
  placeName,
  dateTime,
  performer,
  services,
  label,
  status,
  src,
  onClick,
  leftPadding,
  rightPadding,
  underline,
}: BookingListItemProps) => {
  const statusLabel = bookingStatusLabelMap[status] || null;

  const getLeftSlot = () => {
    if (companyAvatarPosition === 'left') {
      return <CompanyAvatar alt={image?.alt ?? ''} size="md" imgUrl={image?.src ?? ''} />;
    }

    return null;
  };

  const getRightSlot = () => {
    if (companyAvatarPosition === 'right') {
      return <CompanyAvatar alt={image?.alt ?? ''} size="md" imgUrl={image?.src ?? ''} />;
    }

    if (companyAvatarPosition === 'left' && !!src) {
      return <Icon variant="chevron-s-right" />;
    }

    return null;
  };

  return (
    <ListItem
      leftSlot={getLeftSlot()}
      onClick={onClick}
      verticalAlign="top"
      to={src}
      underline={underline}
      leftPadding={leftPadding}
      rightPadding={rightPadding}
      rightSlot={getRightSlot()}>
      <BookingListContent
        placeName={placeName}
        performerAvatar={performerAvatar}
        performer={performer}
        services={services}
        dateTime={dateTime}
        label={label ?? statusLabel}
      />
    </ListItem>
  );
};

export default BookingListItem;
